import { z } from 'zod';

import { zGoDate } from '../helpers/fix-go-types';

export enum UploadStatus {
  Pending = 'pending',
  Finding = 'finding',
  Compressing = 'compressing',
  Uploading = 'uploading',
  Uploaded = 'uploaded',
  Processing = 'processing',
  Completed = 'completed',
  UploadFailed = 'upload_failed',
  ProcessingFailed = 'processing_failed',
}

// isNightlyTrigger reports whether an upload was the automated once-a-night run
// rather than somebody asking for one. Mirrors UploadTrigger.IsNightly in Go —
// both triggers are the nightly, so anything that treats a nightly as background
// noise (muted timeline entries, the onboarding activity signal) must accept
// either.
export const isNightlyTrigger = (trigger: string): boolean =>
  trigger === UploadTrigger.Scheduled || trigger === UploadTrigger.Auto;

export const isFailedStatus = (status: UploadStatus): boolean =>
  status === UploadStatus.UploadFailed || status === UploadStatus.ProcessingFailed;

export const isTerminalStatus = (status: UploadStatus): boolean =>
  status === UploadStatus.Completed || isFailedStatus(status);

export enum UploadTrigger {
  Scheduled = 'scheduled',
  Manual = 'manual',
  Remote = 'remote',
  // Started by the backend after noticing the store's nightly never ran. A
  // nightly as far as the owner is concerned, which is why it shares a label.
  Auto = 'auto',
}

export const UploadSchema = z.object({
  store_id: z.string(),
  upload_id: z.string(),

  trigger: z.enum(UploadTrigger),
  status: z.enum(UploadStatus),

  machine_id: z.string(),
  s3_key: z.string().optional(),
  error_message: z.string().optional(),

  // Both pointers on the Go side and omitempty, so they are absent rather than
  // null while the phase they mark hasn't happened yet.
  started_at: zGoDate,
  upload_finished_at: zGoDate.optional(),
  processing_started_at: zGoDate.optional(),
  // When the worker stopped working on this upload. Absent on records written
  // before the field existed — see the fallback in UploadRow.
  processing_finished_at: zGoDate.optional(),
  updated_at: zGoDate,

  // Computed server-side from the upload lease. The client must not re-derive
  // "is an upload running" from timestamps — that is how the button drifted
  // out of agreement with the API in the first place.
  is_active: z.boolean().default(false),

  // Computed server-side too, and for the same reason: a record that is
  // non-terminal but will never move again. Not derivable from is_active —
  // pending holds no lease, so it reads inactive from the instant it is
  // created, whether or not anyone is about to answer it. See Upload.IsStalled.
  // Defaults false so a response from an API that predates the field parses.
  is_stalled: z.boolean().default(false),
});

export type Upload = z.infer<typeof UploadSchema>;
